import { OpenInNew, TaskAltOutlined } from "@mui/icons-material";
import {
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { AiFillDelete } from "react-icons/ai";
import {
  AccountMetricTile,
  absMoney,
  dueLabel,
  dueTone,
  effectiveDueHint,
  money,
  profitLabel,
  profitTone,
} from "./accountEditorUi";

/**
 * Card for a single account entry (admission / linked module / custom).
 * metricSet:
 *  - "full"    — agreed, paid, due, agent cost, [MARA, referral], commission?, P&L
 *  - "derived" — paid, due, P&L only
 */
const AccountModuleEntryCard = ({
  index,
  title,
  subtitle,
  meta = [],
  agreed,
  paid,
  due,
  /** Due on the terms reached so far; shown under Due when it differs. */
  effectiveDue,
  cost,
  mara,
  referral,
  /** MARA fee — Visa / Skill Assessment only. */
  showMara = false,
  showReferral = false,
  /** @deprecated Prefer showMara + showReferral. When true, shows both. */
  showMaraReferral = false,
  commission,
  showCommission = false,
  commissionCompleted = false,
  onToggleCommissionCompleted,
  commissionToggleBusy = false,
  metricSet = "full",
  profitLoss,
  completed = false,
  pendingCount = 0,
  /** Opens the verifications waiting on THIS record. Without it the chip is just a badge. */
  onPendingClick,
  onOpen,
  openLabel = "Manage",
  /** Click handler for the course/title text (opens EyeModal course details drawer). */
  onTitleClick,
  /** When set, the title renders as an inline text field (e.g. Others entry name). */
  onTitleChange,
  titlePlaceholder = "Name this entry",
  /** Opens the admission course / placement details drawer (EyeModal). */
  onOpenCourseDetails,
  courseDetailsLabel = "Course details",
  /** Navigates to the linked admission application form. */
  onOpenAdmission,
  openAdmissionLabel = "Open admission",
  onOpenPayments,
  paymentsVariant,
  paymentsDisabled = false,
  paymentsTooltip,
  onOpenCharges,
  /** When true with onOpenCharges, the Paid tile opens figures (Others). Otherwise Paid opens Payments. */
  figuresOnPaid = false,
  onOpenCommission,
  commissionActionLabel = "Commission",
  commissionPrimary = false,
  onOpenNotes,
  onOpenExternal,
  onDelete,
  deleteDisabled = false,
  deleteTitle,
  actions,
  children,
}) => {
  const dueNum = Number(due || 0);
  const profitNum = Number(profitLoss || 0);
  const showMaraMetric = showMara || showMaraReferral;
  const showReferralMetric = showReferral || showMaraReferral;
  const paymentsIsPrimary =
    paymentsVariant === "contained" || (!onOpen && !commissionPrimary && Boolean(onOpenPayments));

  // Agreed / Agent cost open the figures popup when editing is available — the tile "Edit"
  // label is what makes that discoverable without hunting for the footer button.
  const editFigures = onOpenCharges
    ? { onClick: onOpenCharges, actionHint: "Edit account figures" }
    : null;
  const paidAction =
    figuresOnPaid && onOpenCharges
      ? editFigures
      : onOpenPayments
      ? { onClick: onOpenPayments, actionHint: "Payments" }
      : editFigures;

  const metrics =
    metricSet === "derived"
      ? [
          { label: "Paid", value: money(paid), ...paidAction },
          {
            label: dueLabel(dueNum),
            value: absMoney(dueNum),
            tone: dueTone(dueNum),
            emphasize: dueNum !== 0,
          },
          {
            label: profitLabel(profitNum),
            value: absMoney(profitNum),
            tone: profitTone(profitNum),
            emphasize: true,
          },
        ]
      : [
          { label: "Agreed", value: money(agreed), ...editFigures },
          { label: "Paid", value: money(paid), ...paidAction },
          {
            label: dueLabel(dueNum),
            value: absMoney(dueNum),
            tone: dueTone(dueNum),
            emphasize: dueNum !== 0,
            hint: effectiveDueHint(dueNum, effectiveDue),
          },
          { label: "Agent cost", value: money(cost), ...editFigures },
          ...(showMaraMetric ? [{ label: "MARA", value: money(mara), ...editFigures }] : []),
          ...(showReferralMetric
            ? [{ label: "Referral", value: money(referral), ...editFigures }]
            : []),
          ...(showCommission
            ? [{ label: "Commission received", value: money(commission), ...editFigures }]
            : []),
          {
            label: profitLabel(profitNum),
            value: absMoney(profitNum),
            tone: profitTone(profitNum),
            emphasize: true,
          },
        ];

  const metricCols =
    metrics.length >= 7
      ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-7"
      : metrics.length >= 6
      ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
      : metrics.length === 5
      ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
      : metrics.length === 4
      ? "grid-cols-2 lg:grid-cols-4"
      : "grid-cols-1 sm:grid-cols-3";

  const indexLabel = typeof index === "number" && index > 0 ? String(index).padStart(2, "0") : null;

  const titleNode = onTitleChange ? (
    <TextField
      size="small"
      variant="standard"
      value={title ?? ""}
      onChange={(e) => onTitleChange(e.target.value)}
      placeholder={titlePlaceholder}
      fullWidth
      inputProps={{ "aria-label": "Entry title" }}
      sx={{
        maxWidth: 420,
        "& .MuiInputBase-input": {
          fontSize: "0.875rem",
          fontWeight: 600,
          py: 0.25,
        },
      }}
    />
  ) : onTitleClick ? (
    <button
      type="button"
      onClick={onTitleClick}
      className="text-sm font-semibold text-primary m-0 break-words text-left underline decoration-primary/40 hover:decoration-primary bg-transparent border-0 p-0 cursor-pointer"
    >
      {title}
    </button>
  ) : (
    <h4 className="text-sm font-semibold text-app-text m-0 break-words">{title}</h4>
  );

  return (
    <div
      className={`rounded-xl border bg-app-surface p-4 shadow-sm transition-colors ${
        completed
          ? "border-emerald-300/80 bg-emerald-50/40"
          : pendingCount > 0
          ? "border-amber-300/80 bg-amber-50/30"
          : "border-app-border hover:border-primary/40"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 flex items-start gap-3">
          {indexLabel ? (
            <span
              className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg text-xs font-bold tracking-wide border ${
                completed
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : pendingCount > 0
                  ? "bg-amber-100 text-amber-800 border-amber-200"
                  : "bg-app-surface-muted text-app-text border-app-border"
              }`}
              aria-hidden
            >
              {indexLabel}
            </span>
          ) : null}

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {titleNode}
              {completed && (
                <Chip size="small" color="success" label="Completed" sx={{ height: 22 }} />
              )}
              {pendingCount > 0 && (
                <Tooltip title={onPendingClick ? "Review what is waiting on this record" : ""}>
                  <Chip
                    size="small"
                    color="warning"
                    icon={<TaskAltOutlined sx={{ fontSize: 14 }} />}
                    label={`${pendingCount} pending`}
                    sx={{ height: 22 }}
                    clickable={Boolean(onPendingClick)}
                    onClick={
                      onPendingClick
                        ? (event) => {
                            event.stopPropagation();
                            onPendingClick();
                          }
                        : undefined
                    }
                  />
                </Tooltip>
              )}
            </div>
            {/* {subtitle ? (
              <p className="text-xs text-app-text-muted m-0 mt-1 break-words">{subtitle}</p>
            ) : null} */}
            {meta.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                {meta.map((item) => (
                  <span key={item.label} className="text-[11px] text-app-text-secondary">
                    <span className="font-semibold text-app-text-muted">{item.label}: </span>
                    {item.value || "—"}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0.5 shrink-0">
          {onOpenExternal && (
            <Tooltip title="Open source record">
              <IconButton
                size="small"
                color="primary"
                onClick={onOpenExternal}
                aria-label="Open source record"
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={deleteTitle || "Delete entry"}>
              <span>
                <IconButton
                  size="small"
                  onClick={onDelete}
                  disabled={deleteDisabled}
                  aria-label={deleteTitle || "Delete entry"}
                >
                  <AiFillDelete className={deleteDisabled ? "text-gray-300" : "text-red-600"} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      {metrics.length > 0 && (
        <div className={`grid gap-2 mt-3 ${metricCols}`}>
          {metrics.map((item) => (
            <AccountMetricTile key={item.label} {...item} />
          ))}
        </div>
      )}

      {onToggleCommissionCompleted && (
        <div className="flex justify-end mt-1">
          <Tooltip
            title={
              commissionCompleted
                ? "Reopen to record another month of commission"
                : "No more commission is coming for this record"
            }
          >
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  size="small"
                  checked={Boolean(commissionCompleted)}
                  disabled={commissionToggleBusy}
                  onChange={(event) => onToggleCommissionCompleted(event.target.checked)}
                />
              }
              label={
                <span className="text-[11px] text-app-text-secondary">Commission completed</span>
              }
            />
          </Tooltip>
        </div>
      )}

      {(onOpen ||
        onOpenCourseDetails ||
        onOpenAdmission ||
        onOpenPayments ||
        onOpenCharges ||
        onOpenCommission ||
        onOpenNotes ||
        actions) && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-app-border/70">
          {onOpen && (
            <Button size="small" variant="contained" onClick={onOpen}>
              {openLabel}
            </Button>
          )}
          {onOpenCourseDetails && (
            <Button size="small" variant="outlined" onClick={onOpenCourseDetails}>
              {courseDetailsLabel}
            </Button>
          )}
          {onOpenAdmission && (
            <Button size="small" variant="outlined" onClick={onOpenAdmission}>
              {openAdmissionLabel}
            </Button>
          )}
          {onOpenPayments && (
            <Tooltip title={paymentsTooltip || ""}>
              <span>
                <Button
                  size="small"
                  variant={paymentsIsPrimary ? "contained" : "outlined"}
                  disabled={paymentsDisabled}
                  onClick={onOpenPayments}
                >
                  Payments
                </Button>
              </span>
            </Tooltip>
          )}
          {onOpenCharges && (
            <Button size="small" variant="outlined" onClick={onOpenCharges}>
              Edit account figures
            </Button>
          )}
          {onOpenCommission && (
            <Button
              size="small"
              variant={commissionPrimary ? "contained" : "outlined"}
              onClick={onOpenCommission}
            >
              {commissionActionLabel}
            </Button>
          )}
          {onOpenNotes && (
            <Button size="small" variant="text" onClick={onOpenNotes}>
              Notes
            </Button>
          )}
          {actions}
        </div>
      )}

      {children}
    </div>
  );
};

export default AccountModuleEntryCard;
